import React from 'react';

import type * as api from '@gitbook/api-client';
import { createPalette, type PaletteDef } from '@gitbook/react-uikit';
import { DialogRoot } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiResource, readAPICache } from '../../../state';
import { IntegrationContentSourceIcon } from '../../Integrations';
import { ConfigureNewContentIntegrationSourceDialog } from './ConfigureNewContentIntegrationSourceDialog';

/**
 * Create a palette to pick a content source.
 */
export function usePickIntegrationContentSourcePalette(props: {
    organizationId: string;
    spaceId: string;
    onSubmit: (source: api.ComputedContentSourceIntegration) => void;
}): [React.ReactNode, () => PaletteDef] {
    const { organizationId, spaceId, onSubmit } = props;
    const [selected, setSelected] = React.useState<{
        integration: api.Integration;
        contentSource: api.IntegrationContentSource;
    } | null>(null);

    const getPalette = useEventCallback(() => {
        return createPalette({
            groups: [
                {
                    key: 'content-sources',
                    commands: () => {
                        const result = readAPICache(
                            apiResource('listOrganizationIntegrations', {
                                organizationId,
                                limit: 1000,
                                contentSources: true,
                            })
                        );

                        return getContentSourcesCommands({
                            integrations: result.items,
                            onTrigger: ({ integration, contentSource }) => {
                                setSelected({ integration, contentSource });
                            },
                        });
                    },
                },
            ],
        });
    });

    return [
        <DialogRoot
            key="dialog"
            isOpen={selected !== null}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setSelected(null);
                }
            }}
        >
            {selected ? (
                <ConfigureNewContentIntegrationSourceDialog
                    organizationId={organizationId}
                    spaceId={spaceId}
                    onSubmit={(computed) => {
                        setSelected(null);
                        onSubmit(computed);
                    }}
                    selected={selected}
                />
            ) : null}
        </DialogRoot>,
        getPalette,
    ];
}

/**
 * Get commands to pick a content source from a list of integrations.
 */
function getContentSourcesCommands(args: {
    integrations: api.Integration[];
    onTrigger: (props: {
        integration: api.Integration;
        contentSource: api.IntegrationContentSource;
    }) => void;
}) {
    return args.integrations
        .flatMap((integration) =>
            (integration.contentSources ?? []).map((contentSource) => ({
                integration,
                contentSource,
            }))
        )
        .map(({ integration, contentSource }) => ({
            key: `${integration.name}-${contentSource.id}`,
            title: contentSource.title,
            icon: (
                <IntegrationContentSourceIcon
                    integration={integration}
                    contentSource={contentSource}
                />
            ),
            trigger: () => {
                args.onTrigger({ integration, contentSource });
            },
        }));
}
